import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Quote, Newspaper, ArrowRight } from "lucide-react";
import { api, publicFileUrl } from "@/lib/api";
import InterviewModal from "@/components/InterviewModal";

const TONE_META = {
  fiducia:       { label: "FIDUCIA",       chip: "PRIMO PIANO", color: "#0ea5e9", emoji: "💪", gradient: "linear-gradient(135deg,#38bdf8 0%,#0284c7 100%)" },
  rabbia:        { label: "SFOGO",         chip: "SFOGO",       color: "#ef4444", emoji: "🔥", gradient: "linear-gradient(135deg,#f97316 0%,#dc2626 100%)" },
  ironia:        { label: "SFOTTÒ",        chip: "SFOTTÒ",      color: "#f59e0b", emoji: "😏", gradient: "linear-gradient(135deg,#fbbf24 0%,#f59e0b 100%)" },
  dichiarazione: { label: "ESCLUSIVA",     chip: "ESCLUSIVA",   color: "#3b56e0", emoji: "📣", gradient: "linear-gradient(135deg,#6366f1 0%,#3b56e0 100%)" },
  disperazione:  { label: "DISPERAZIONE",  chip: "DISPERAZIONE", color: "#8b5cf6", emoji: "😭", gradient: "linear-gradient(135deg,#a78bfa 0%,#6d28d9 100%)" },
  resa:          { label: "RESA",          chip: "RESA",        color: "#64748b", emoji: "🏳️", gradient: "linear-gradient(135deg,#94a3b8 0%,#475569 100%)" },
};

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "FIFE";
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s fa`;
  if (s < 3600) return `${Math.floor(s / 60)}m fa`;
  if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
  return `${Math.floor(s / 86400)}g fa`;
}

function InterviewVisual({ interview, size = "lg" }) {
  const t = TONE_META[interview.tone] || TONE_META.dichiarazione;
  const dims = size === "lg" ? "aspect-[16/10]" : "w-24 h-24 md:w-32 md:h-32";
  const initSize = size === "lg" ? "text-6xl md:text-8xl" : "text-2xl md:text-3xl";
  const img = interview.image_path ? publicFileUrl(interview.image_path) : null;
  return (
    <div className={`${dims} relative overflow-hidden rounded-xl shrink-0`} style={{ background: t.gradient }} data-testid={`interview-visual-${interview.id || interview.tone}`}>
      {img ? (
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <>
          <div className="absolute inset-0 opacity-20"
               style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 12px, transparent 12px 28px)" }} />
          <Quote className="absolute top-3 left-3 text-white/40" style={{ width: size === "lg" ? 44 : 18, height: size === "lg" ? 44 : 18 }} />
          <div className={`absolute inset-0 flex items-center justify-center display font-black text-white ${initSize}`} style={{ textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>
            {initials(interview.team_name)}
          </div>
        </>
      )}
      {size === "lg" && (
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-white/95" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>Sala stampa FIFE</span>
          <span className="text-lg">{t.emoji}</span>
        </div>
      )}
    </div>
  );
}

export default function InterviewsSection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/public/interviews?limit=4")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const featured = items[0];
  const side = items.slice(1, 4);

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="interviews-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#3b56e018", color: "#3b56e0" }}>
            <Newspaper className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Sala stampa FIFE</div>
            <h2 className="display font-black text-2xl md:text-3xl tracking-tight">Interviste</h2>
          </div>
        </div>
        <Link
          to="/news"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-black uppercase tracking-widest text-slate-900 border border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
          data-testid="interviews-see-all"
        >
          Vai a tutti gli articoli
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="rounded-3xl p-6 md:p-10" style={{ background: "#f7ead2" }}>
        <div className="text-slate-900/70 display font-bold text-lg md:text-xl mb-6">Primo piano</div>

        {loaded && items.length === 0 ? (
          <div className="rounded-2xl bg-white/70 p-10 text-center" data-testid="interviews-empty">
            <Mic className="w-8 h-8 mx-auto mb-3 text-slate-400" />
            <div className="display font-bold text-slate-800">Ancora nessuna conferenza stampa.</div>
            <div className="text-slate-600 text-sm mt-1">
              I fantallenatori possono convocarne una dalla loro <Link to="/dashboard" className="text-[var(--brand)] font-semibold hover:underline">area riservata</Link>.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 md:gap-10">
            {featured && (
              <article className="group cursor-pointer text-left" data-testid={`interview-featured-${featured.id}`}>
                <button type="button" onClick={() => setSelected(featured)} className="block w-full text-left" data-testid={`interview-open-${featured.id}`}>
                  <InterviewVisual interview={featured} size="lg" />
                  <div className="mt-5">
                    <div className="text-center">
                      <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                        {(TONE_META[featured.tone] || TONE_META.dichiarazione).chip}
                      </span>
                    </div>
                    <h3 className="display font-black text-2xl md:text-3xl lg:text-4xl leading-tight text-center text-slate-900 mt-3 max-w-2xl mx-auto group-hover:underline decoration-slate-400 underline-offset-4 break-words">
                      {featured.team_name && <span className="mr-2">{featured.team_name}:</span>}
                      <span className="italic">«{featured.headline}»</span>
                    </h3>
                    {featured.quote && (
                      <p className="text-slate-700 text-sm md:text-base mt-3 text-center max-w-2xl mx-auto leading-relaxed line-clamp-3 break-words [overflow-wrap:anywhere]">
                        {featured.quote}
                      </p>
                    )}
                    <div className="mt-4 text-center text-slate-500 text-sm">
                      {featured.coach_name || "Fantallenatore"} · Girone {featured.girone} · {timeAgo(featured.created_at)}
                    </div>
                  </div>
                </button>
              </article>
            )}

            <div className="flex flex-col divide-y divide-slate-900/10">
              {side.map((it, idx) => {
                const t = TONE_META[it.tone] || TONE_META.dichiarazione;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setSelected(it)}
                    className={`grid grid-cols-[auto_1fr] gap-4 text-left w-full ${idx === 0 ? "pb-5" : "py-5"} ${idx === side.length - 1 ? "pb-0" : ""} group`}
                    data-testid={`interview-side-${it.id}`}
                  >
                    <InterviewVisual interview={it} size="sm" />
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t.chip}</div>
                      <h4 className="display font-black text-base md:text-lg leading-snug text-slate-900 mt-1 underline decoration-slate-400/60 underline-offset-2 group-hover:decoration-slate-900 transition break-words">
                        <span className="mr-1">{it.team_name}:</span>
                        <span className="italic">«{it.headline}»</span>
                      </h4>
                      <div className="text-slate-500 text-xs mt-2 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-white text-[10px] font-black" style={{ background: t.color }}>
                          {t.emoji}
                        </span>
                        <span>{it.coach_name || "Fantallenatore"}</span>
                        <span className="text-slate-400">· {timeAgo(it.created_at)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}

              {side.length === 0 && (
                <div className="text-slate-500 text-sm italic">Servono almeno 2 conferenze stampa per mostrare la colonna laterale.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <InterviewModal interview={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
